import { Box, Button, Chip, Pagination } from "@mui/material";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AdminNotification, NotificationService } from "../../api/notificationService";
import Card from "../../components/common/Card";

const PAGE_SIZE = 10;

const Notifications = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [items, setItems] = useState<AdminNotification[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);

  const fetchData = useCallback(async () => {
    const result = await NotificationService.list(page, PAGE_SIZE);
    setItems(result.items);
    setTotal(result.total);
  }, [page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const markRead = async (item: AdminNotification) => {
    if (!item.read) {
      await NotificationService.markRead(item.id);
      fetchData();
    }
  };

  const markAllRead = async () => {
    await NotificationService.markAllRead();
    fetchData();
  };

  const openSalon = (item: AdminNotification) => {
    const outletId = item.data?.outlet_id ?? item.outletId;
    if (!outletId) return;
    navigate(
      item.data?.awaiting_review ? `/salon/registration/${outletId}` : `/salon/${outletId}`
    );
  };

  return (
    <Card>
      <Box display="flex" justifyContent="space-between" mb={2}>
        <h2 className="font-bold text-lg">{t("notification.title")}</h2>
        <Button onClick={markAllRead}>{t("notification.mark_all")}</Button>
      </Box>
      {items.length === 0 && <div>{t("notification.empty")}</div>}
      {items.map((item) => (
        <Box
          key={item.id}
          onClick={() => markRead(item)}
          sx={{
            p: 2,
            mb: 2,
            border: "1px solid #eee",
            borderRadius: 2,
            background: item.read ? "transparent" : "rgba(255,193,7,.12)",
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <b>{item.title}</b>
            <span>{moment(item.createdDate).format("YYYY/MM/DD HH:mm")}</span>
          </Box>
          <div>{item.body}</div>
          {item.data?.awaiting_review && (
            <Chip size="small" color="warning" label={t("notification.waiting_review")} sx={{ my: 1 }} />
          )}
          {!!item.data?.changes?.length && (
            <table style={{ borderCollapse: "collapse", marginTop: 8, width: "100%" }}>
              <thead>
                <tr>
                  {["field", "before", "after"].map((head) => (
                    <th key={head} style={{ border: "1px solid #ddd", padding: 6, textAlign: "start" }}>
                      {t(`notification.col.${head}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {item.data.changes.map((change) => (
                  <tr key={change.field}>
                    <td style={{ border: "1px solid #ddd", padding: 6 }}>{change.label}</td>
                    <td style={{ border: "1px solid #ddd", padding: 6 }}>{change.old_value || "-"}</td>
                    <td style={{ border: "1px solid #ddd", padding: 6 }}>{change.new_value || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Button size="small" sx={{ mt: 1 }} onClick={() => openSalon(item)}>
            {t("notification.open_salon")}
          </Button>
        </Box>
      ))}
      <Pagination
        count={Math.ceil(total / PAGE_SIZE)}
        page={page + 1}
        onChange={(_, value) => setPage(value - 1)}
      />
    </Card>
  );
};

export default Notifications;
